/**
 * ViewAsBanner — app-wide strip shown while an owner/admin is "viewing as"
 * another user (read-only impersonation). Rendered at the top of <main> so it
 * stays visible on every screen, making it impossible to forget you're seeing
 * someone else's lens. The Exit button drops back to your own identity.
 *
 * The whole console already re-renders against the target's role/permissions
 * (see AuthContext effectiveUser) — this banner is the always-present signal +
 * escape hatch.
 *
 * Two things are NOT read-only: zone create/edit/delete and rate saves (see
 * hasPerm's edit.zones / edit.rates carve-outs in AuthContext) — so the badge
 * reflects whichever of those the REAL operator holds instead of claiming a
 * blanket guarantee the console no longer holds.
 */
import { useAuth } from '../auth/AuthContext'
import { ROLE_LABELS } from '../auth/permissions'

export default function ViewAsBanner() {
  const { viewingAs, realUser, stopViewingAs, hasPerm } = useAuth()
  if (!viewingAs) return null
  const canEditZones = hasPerm('edit.zones')
  const canEditRates = hasPerm('edit.rates')
  const editableParts = [canEditZones && 'zones', canEditRates && 'rates'].filter(Boolean)

  const who = viewingAs.name?.trim() || viewingAs.email
  const roleLabel = ROLE_LABELS[viewingAs.role] ?? viewingAs.role
  const tenant = viewingAs.client?.name
    ?? (viewingAs.clientId == null ? 'Parkify staff' : null)

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        margin: '0 0 18px', padding: '10px 16px',
        // Pinned to the top of the scrolling <main> so it can't scroll away:
        // the way back must be one click from anywhere on the page.
        position: 'sticky', top: 0, zIndex: 50,
        borderRadius: 'var(--radius-md, 10px)',
        background: 'linear-gradient(90deg, color-mix(in srgb, var(--parkify-accent) 12%, transparent), rgba(245,158,11,0.12)), var(--color-white, #fff)',
        border: '1px solid color-mix(in srgb, var(--parkify-accent) 35%, transparent)',
        fontSize: 13, lineHeight: 1.4, color: '#231F20',
      }}
    >
      <EyeIcon />
      <span style={{ fontWeight: 700 }}>
        Viewing as {who}
      </span>
      <span style={{ color: 'var(--color-gray-700)' }}>
        {roleLabel}{tenant ? ` · ${tenant}` : ''}
      </span>
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
        padding: '2px 8px', borderRadius: 999,
        background: 'rgba(245,158,11,0.18)', color: '#92400e',
      }}>
        {editableParts.length > 0 ? `Read-only · ${editableParts.join(' & ')} editable` : 'Read-only'}
      </span>
      <span style={{ flex: 1 }} />
      {realUser && (
        <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
          You are {realUser.name?.split(' ')[0] ?? realUser.email}
        </span>
      )}
      <button
        onClick={stopViewingAs}
        style={{
          padding: '6px 14px', borderRadius: 'var(--radius-btn)', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 700, color: 'var(--color-white)',
          background: 'var(--parkify-accent)', border: 'none',
        }}
      >
        Exit view
      </button>
    </div>
  )
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--parkify-accent)"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}
